import React from 'react'
import { type Row } from '@tanstack/react-table'
import { get } from 'lodash'
import moment from 'moment'

import { Button, Icon, Mdash, trimText } from '../../../../module'
import styles from '../../SpreadsheetTable/SpreadsheetCell/_spreadsheet-cell.module.scss'
import {
  type InputRule,
  isBoolean,
  isSelect,
  isValidDate,
  type TypeCustomRules,
} from '../../SpreadsheetTable/SpreadsheetCell/validationService'
import { type IconColorList } from '../../../Icons/Icon.models'

export type Column = {
  id: string
  columnDef: {
    header?: {
      heading?: string
    }
    meta?: {
      editable?: boolean
      disableTrimText?: boolean
      sidebarContent?: unknown
    }
    accessorKey: string
  }
}

export type SpreadsheetCellProps<TData extends object> = {
  getValue: () => string
  table: {
    options: {
      meta?: {
        inputRules: {
          [key: string]: InputRule
        }
        isReviewMode: boolean
        updateData: (
          rowIndex: number,
          columnId: string,
          value: string | number | boolean | Date,
        ) => void
      }
    }
  }
  row: Row<TData> & {
    original: {
      editable: boolean
      disableTrimText?: boolean
      sidebarContent?: unknown
      isOptional?: boolean
      color?: IconColorList
    }
  }
  column: Column
  /** Optional children can be passed in for custom JSX content */
  children?: React.ReactNode
}

const SpreadsheetCell = <TData extends object>({
  table,
  row,
  column,
  children,
}: SpreadsheetCellProps<TData>) => {
  const cellKey = column.columnDef.accessorKey.split('.')[0],
    inputRules = table.options?.meta?.inputRules
  const getObjectByKey = (key: string) => {
    return inputRules && inputRules?.[key]
  }

  const validationRules = getObjectByKey(column?.id)
  const value = get(row.original, [cellKey, 'value'])
  const icon = get(row.original, [cellKey, 'icon'])
  const color = get(row.original, [cellKey, 'color'])
  const isOptional = get(row.original, [cellKey, 'isOptional']) ?? false
  const isReviewMode = table.options?.meta?.isReviewMode

  const isDisableTrimText =
    get(row.original, [cellKey, 'disableTrimText']) ?? false

  // Focusing the input field on open
  const TRIM_TEXT_CHARACTER_LENGTH = 25

  const getValueBykey = (options: TypeCustomRules, key: string) => {
    return options?.[key] ?? key
  }

  const isSelectRule = isSelect(validationRules)
  const isBooleanRule = isBoolean(validationRules)
  const isValidDateFormat = isValidDate(value, validationRules)

  return (
    <>
      {children ? (
        <div className={styles.cell}>{children}</div>
      ) : (
        <Button as='unstyled'>
          <div
            className={`${styles.cellWrapper} ${!isReviewMode ? `fc-${color}` : ''}`}
          >
            {!isReviewMode && value?.toString()?.length === 0 && isOptional ? (
              <Mdash />
            ) : (
              <>
                {!isReviewMode && icon && (
                  <Icon icon={icon} color={color} iconSize='16px' />
                )}
                {isValidDateFormat
                  ? moment(value).format('YYYY-MM-DD')
                  : isSelectRule || isBooleanRule
                    ? getValueBykey(
                        validationRules?.options as TypeCustomRules,
                        value,
                      )
                    : isDisableTrimText
                      ? value
                      : trimText(value?.toString(), TRIM_TEXT_CHARACTER_LENGTH)}
              </>
            )}
          </div>
        </Button>
      )}
    </>
  )
}

export default SpreadsheetCell
